import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Sparkles, Home, Users, CreditCard, User, LogOut, Menu, X, Bell, Gift, ShieldCheck } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getUnreadNotificationCount } from '@/services/api';

export const DashboardLayout = ({ children }) => {
  const { user, logout, isSubscribed } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await getUnreadNotificationCount();
        setUnreadCount(data.count);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    
    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/referrals', label: 'Referrals', icon: Gift },
    { path: '/subscription', label: 'Subscription', icon: CreditCard },
    // Only admins see this; the admin pages also check on their own.
    ...(user?.is_admin ? [{ path: '/admin', label: 'Admin', icon: ShieldCheck }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Top Nav - Desktop */}
      <nav className="hidden md:block sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/dashboard" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="font-heading text-xl font-semibold text-stone-900">BeautyPro</span>
              </Link>
              
              <div className="flex items-center gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-stone-900 text-white'
                        : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications Bell */}
              <Link to="/notifications" className="relative" data-testid="notifications-link">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className={`text-stone-500 hover:text-stone-900 ${isActive('/notifications') ? 'bg-stone-100' : ''}`}
                >
                  <Bell className="w-5 h-5" />
                </Button>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#D97757] text-white text-xs flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* Profile Link */}
              <Link to="/profile" data-testid="profile-link">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className={`text-stone-500 hover:text-stone-900 ${isActive('/profile') ? 'bg-stone-100' : ''}`}
                >
                  <User className="w-5 h-5" />
                </Button>
              </Link>

              {/* Upgrade button hidden - app is currently free
              {!isSubscribed && (
                <Link to="/subscription">
                  <Button 
                    size="sm" 
                    className="h-9 px-4 rounded-full bg-[#D97757] text-white hover:bg-[#C06040] text-sm"
                    data-testid="upgrade-btn"
                  >
                    Upgrade
                  </Button>
                </Link>
              )}
              */}
              
              <div className="flex items-center gap-3 pl-3 border-l border-stone-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-stone-900">{user?.full_name}</p>
                  <p className="text-xs text-stone-500">Pro Member</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleLogout}
                  className="text-stone-500 hover:text-stone-900"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <nav className="md:hidden sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-lg font-semibold text-stone-900">BeautyPro</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/notifications" className="relative p-2">
              <Bell className="w-5 h-5 text-stone-600" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 w-4 h-4 rounded-full bg-[#D97757] text-white text-[10px] flex items-center justify-center font-medium">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-stone-200 shadow-lg">
            <div className="p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActive(item.path)
                      ? 'bg-stone-900 text-white'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
              <Link
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                  isActive('/profile')
                    ? 'bg-stone-900 text-white'
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <User className="w-5 h-5" />
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-stone-600 hover:bg-stone-100 w-full"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden mobile-nav bg-white border-t border-stone-200 pb-safe">
        <div className="flex justify-around py-2">
          {[...navItems.slice(0, 3), { path: '/profile', label: 'Profile', icon: User }].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${
                isActive(item.path) ? 'text-[#D97757]' : 'text-stone-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="pb-20 md:pb-8">
        {children}
      </main>
    </div>
  );
};
